<template>
  <div class="do-page f-r">
    <div class="infos f-c">
      <order-info
        :order="order"
        :editable="false"
        :mode="FormModes.SINGLE"
        :orderId="orderId"
      />
      <picking-up-info
        :container-id="containerId"
        :initial-editing="true"
        :mode="FormModes.SINGLE"
        :value="pickUpInfo"
        @input="update"
        @resetPickUpInfo="reset"
      />
      <div class="features f-r-vc">
        <el-button
          type="primary"
          class="feature-button hash-brown-bg"
          @click="downloadDo"
        >
          <svg-icon icon-class="info" class="svg-white-color" />
          Download DO
        </el-button>
        <el-button
          type="primary"
          class="feature-button deep-green-bg"
          @click="openSendEmail"
        >
          <svg-icon icon-class="email" class="svg-white-color" />
          Send Email
        </el-button>
      </div>
    </div>
    <common-card class="do-preview-container">
      <do-template
        :shipment-info-template="shipmentInfoTemplate"
        :container-info-template="containerInfoTemplate"
        :do-form="doForm"
        id="do-template"
      />
    </common-card>
    <send-email ref="sendEmail" />
  </div>
</template>
<script>
// apis
import { detailOrder } from '@/api/order'
import { sendEmail } from '@/api/mail'
import { queryGoods, calcTotal } from '@/api/goods'
import { createPdfFile, downloadPdf } from '@/core/utils/pdf'
// models
import {
  shipmentInfoTemplate,
  containerInfoTemplate,
  companyInfo,
  composeTruckCompanyInfo,
  composeDirectDeliveryInfo
} from '@/core/models/do'
// components
import OrderInfo from '@/components/order/OrderInfo.vue'
import DoTemplate from '@/components/do/DoTemplate.vue'
import CommonCard from '@/components/common/CommonCard.vue'
import PickingUpInfo from '@/components/order/PickingUpInfo.vue'
import SendEmail from '@/components/user/SendEmail.vue'
// others
import { FormModes } from '@/core/configs/types'

export default {
  name: 'DoPage',
  components: {
    DoTemplate,
    CommonCard,
    PickingUpInfo,
    OrderInfo,
    SendEmail
  },
  data() {
    return {
      shipmentInfoTemplate: shipmentInfoTemplate,
      containerInfoTemplate: containerInfoTemplate,
      FormModes,
      order: {},
      // orderId: this.$route.query.id,
      orderId: '',
      orderNo: this.$route.params.no,
      originalPickUpInfo: {},
      pickUpInfo: {},
      doForm: {},
      companyInfo: companyInfo
    }
  },
  methods: {
    update(e) {
      this.$set(this, 'pickUpInfo', { ...this.pickUpInfo, ...e })
    },
    updateCounts(e) {
      this.$set(this.pickUpInfo, 'counts', e)
    },
    updatePickUpInfo(e) {
      this.$set(this, 'pickUpInfo', e)
    },
    async getOrderDetail() {
      const order = await detailOrder(this.orderNo)
      this.order = order
      this.orderId = order.id

      this.originalPickUpInfo = this.order?.container?.pickUpInfo ?? {}
      this.pickUpInfo = this.order?.container?.pickUpInfo ?? {}
      this.$set(
        this.pickUpInfo,
        'pickUpAddress',
        this.order?.container?.pickUpInfo?.pickUpAddress ??
          this.order?.container?.terminalPort
      )
    },
    composeTruckCompanyInfo() {
      const truckCompanyName = this.pickUpInfo?.truckCompanyName
        ? this.pickUpInfo.truckCompanyName + '\n'
        : ''
      const truckCompanyAddress = this.pickUpInfo?.truckCompanyAddress
        ? this.pickUpInfo.truckCompanyAddress + '\n'
        : ''
      const truckCompanyTel = this.pickUpInfo?.truckCompanyTel
        ? 'TEL: ' + this.pickUpInfo.truckCompanyTel + '\n'
        : ''
      const truckCompanyEmail = this.pickUpInfo?.truckCompanyEmail
        ? 'EMAIL: ' + this.pickUpInfo.truckCompanyEmail + '\n'
        : ''
      const truckCompanyFax = this.pickUpInfo?.truckCompanyFax
        ? 'FAX: ' + this.pickUpInfo.truckCompanyFax
        : ''
      return (
        truckCompanyName +
        truckCompanyAddress +
        truckCompanyTel +
        truckCompanyEmail +
        truckCompanyFax
      )
    },
    async setDoBasicInfo() {
      this.$set(
        this.doForm,
        'billTo',
        composeTruckCompanyInfo(this.companyInfo)
      )
      this.$set(this.doForm, 'deliveryRefNo', this.order.riiOrderNo)
      this.$set(this.doForm, 'mbl', this.order.container.mbl)
      this.$set(
        this.doForm,
        'dischargeEta',
        new Date(this.order.container.eta).toLocaleDateString('en-US')
      )
      this.$set(this.doForm, 'containerNo', this.order.container.containerNo)
      const goodsIds = this.order.goodsIds
      const goods = await queryGoods({ ids: goodsIds })

      this.$set(
        this.doForm,
        'weight',
        this.calcTotal(goods.records, 'kilograms')
      )
      this.$set(this.doForm, 'measure', this.calcTotal(goods.records, 'cmb'))
      if (this.order.type === 'Direct') {
        this.$set(
          this.doForm,
          'deliveryTo',
          composeDirectDeliveryInfo(this.order.directDeliveryInfo)
        )
      } else {
        this.$set(
          this.doForm,
          'deliveryTo',
          composeTruckCompanyInfo(this.companyInfo)
        )
      }
    },
    calcTotal,
    async sendEmail(email) {
      const pdf = await this.getPdf('#do-template')
      const file = createPdfFile(pdf, this.order.riiOrderNo + '-do')
      const params = new FormData()
      params.append('file', file, file.name)
      Object.keys(email).forEach(key => {
        params.append(key, email[key])
      })
      const res = await sendEmail(params)
      return [!!res, res.message ?? 'error sending email']
    },
    openSendEmail() {
      this.$refs.sendEmail.open(this.sendEmail)
    },
    async downloadDo() {
      const pdf = await this.getPdf('#do-template')
      downloadPdf(pdf, this.order.riiOrderNo + '-do')
    },
    reset() {
      this.$set(this, 'pickUpInfo', this.originalPickUpInfo)
    }
  },
  mounted() {
    this.getOrderDetail().then(res => {
      this.$route.meta.name = this.order.riiOrderNo + '-DO'
    })
  },
  watch: {
    pickUpInfo: {
      handler() {
        this.$set(this.doForm, 'toTruckingCo', this.composeTruckCompanyInfo())
        this.$set(this.doForm, 'pickUpAddress', this.pickUpInfo?.pickUpAddress)
        this.$set(this.doForm, 'containerSize', this.pickUpInfo?.containerSize)
        this.$set(
          this.doForm,
          'counts',
          this.pickUpInfo?.counts !== undefined
            ? this.pickUpInfo.counts + ' CTN'
            : ''
        )
      },
      deep: true
    },
    order: {
      handler() {
        this.setDoBasicInfo()
      }
    }
  },
  computed: {
    containerId() {
      return this.order?.container?.id ?? ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.do-page {
  .infos {
    min-width: 500px !important;
  }

  .do-preview-container {
    margin-left: 0 !important;
    flex-shrink: 0;
  }

  .features {
    margin: 0 20px;
    margin-top: 15px;
    gap: 20px;

    .feature-button {
      margin-left: 0;
    }
  }
}
</style>
